import {memo, useEffect, useMemo, useState} from 'react';
import {
    Menu, MenuHandler, Button, MenuList, MenuItem,
} from "@material-tailwind/react";
import moment from "moment";
import {ChartPeriod} from "../types/ChartPeriod";
import DatePicker from "./DatePicker";
import {useTranslation} from "react-i18next";

interface DatePickerMenuProps {
    setStart: (s: string) => void,
    setEnd: (s: string) => void,
    onChanged: (start: string, end: string, period: ChartPeriod) => void,
    dateFormat: string,
    startDate: string,
    endDate: string,
    className?: string,
    showFilterButton?: boolean,
    showCustomisationButton?: boolean,
    defaultPeriod?: ChartPeriod
}

const DatePickerMenu = ({
                            onChanged,
                            setEnd,
                            setStart,
                            dateFormat,
                            startDate,
                            endDate,
                            className,
                            defaultPeriod = ChartPeriod.TODAY,
                            showFilterButton = true,
                            showCustomisationButton = true
                        }: DatePickerMenuProps) => {
    const {t} = useTranslation();
    const [localPeriod, setLocalPeriod] = useState<ChartPeriod>(defaultPeriod);

    useEffect(() => {
    }, []);

    const changedPeriod = (period: ChartPeriod) => {
        setLocalPeriod(period);
        switch (period) {
            case ChartPeriod.TODAY:
                setStart(moment().format(dateFormat));
                setEnd(moment().format(dateFormat));
                onChanged(moment().format(dateFormat), moment().format(dateFormat), period);
                break;
            case ChartPeriod.YESTERDAY:
                setStart(moment().subtract(1, 'day').format(dateFormat));
                setEnd(moment().subtract(1, 'day').format(dateFormat));
                onChanged(moment().subtract(1,'day').format(dateFormat), moment().subtract(1,'day').format(dateFormat), period);
                break;
            case ChartPeriod.THIS_WEEK:
                setStart(moment().startOf('week').format(dateFormat));
                setEnd(moment().endOf('week').format(dateFormat));
                onChanged(moment().startOf('week').format(dateFormat), moment().endOf('week').format(dateFormat), period);
                break;
            case ChartPeriod.LAST_WEEK:
                setStart(moment().subtract(1, 'weeks').startOf('week').format(dateFormat));
                setEnd(moment().subtract(1, 'weeks').endOf('week').format(dateFormat));
                onChanged(moment().subtract(1, 'weeks').startOf('week').format(dateFormat), moment().subtract(1, 'weeks').endOf('week').format(dateFormat), period);
                break;
            case ChartPeriod.THIS_MONTH:
                setStart(moment().startOf('month').format(dateFormat));
                setEnd(moment().endOf('month').format(dateFormat));
                onChanged(moment().startOf('month').format(dateFormat), moment().endOf('month').format(dateFormat), period);
                break;
            case ChartPeriod.THIS_YEAR:
                setStart(moment().startOf('year').format(dateFormat));
                setEnd(moment().endOf('year').format(dateFormat));
                onChanged(moment().startOf('year').format(dateFormat), moment().endOf('year').format(dateFormat), period);
                break;
            case ChartPeriod.LAST_YEAR:
                setStart(moment().subtract(1, 'years').startOf('year').format(dateFormat));
                setEnd(moment().subtract(1, 'years').endOf('year').format(dateFormat));
                onChanged(moment().subtract(1, 'years').startOf('year').format(dateFormat), moment().subtract(1, 'years').endOf('year').format(dateFormat), period);
                break;
        }
    }

    const periodLabel = useMemo(() => {
        let text = t('admin.datepicker.custom');
        if (localPeriod === ChartPeriod.TODAY)
            text = t('admin.datepicker.today');
        if (localPeriod === ChartPeriod.YESTERDAY)
            text = t('admin.datepicker.yesterday');
        if (localPeriod === ChartPeriod.THIS_WEEK)
            text = t('admin.datepicker.this_week')
        if (localPeriod === ChartPeriod.LAST_WEEK)
            text = t('admin.datepicker.last_week')
        if (localPeriod === ChartPeriod.THIS_MONTH)
            text = t('admin.datepicker.this_month')
        if (localPeriod === ChartPeriod.THIS_YEAR)
            text = t('admin.datepicker.this_year')
        if (localPeriod === ChartPeriod.LAST_YEAR)
            text = t('admin.datepicker.last_year')

        return text;
    }, [localPeriod]);

    const datepickerUI = useMemo(() => {
        if (localPeriod !== ChartPeriod.CUSTOM)
            return <></>
        return <div
            className={`text-left mt-2 flex flex-col md:flex-row gap-2 ${showFilterButton ? 'mb-2' : ''}`}>
            <DatePicker label={t('admin.datepicker.date_from')} date={startDate} onChange={setStart}/>
            <DatePicker label={t('admin.datepicker.date_to')} date={endDate} minDate={startDate} onChange={setEnd}/>
            {showFilterButton ? <Button className="bg-white text-black dark:bg-gray-500"
                                        onClick={() => onChanged(startDate, endDate, localPeriod)}>{t('admin.datepicker.filter')}</Button> : ''}
        </div>
    }, [localPeriod, startDate, endDate, setEnd, onChanged, setStart, showFilterButton]);

    return (
        <div className={`${className ?? 'mt-6 text-right'}`}>
            {datepickerUI}
            {showCustomisationButton ? <Menu>
                <MenuHandler>
                    <Button className="bg-black snake">{periodLabel}</Button>
                </MenuHandler>
                <MenuList className="bg-white dark:bg-blue-gray-900 dark:text-gray-500 dark:border-blue-gray-900">
                    <MenuItem onClick={() => changedPeriod(ChartPeriod.TODAY)}>{t('admin.datepicker.today')}</MenuItem>
                    <MenuItem onClick={() => changedPeriod(ChartPeriod.YESTERDAY)}>{t('admin.datepicker.yesterday')}</MenuItem>
                    <MenuItem
                        onClick={() => changedPeriod(ChartPeriod.THIS_WEEK)}>{t('admin.datepicker.this_week')}</MenuItem>
                    <MenuItem
                        onClick={() => changedPeriod(ChartPeriod.LAST_WEEK)}>{t('admin.datepicker.last_week')}</MenuItem>
                    <MenuItem
                        onClick={() => changedPeriod(ChartPeriod.THIS_MONTH)}>{t('admin.datepicker.this_month')}</MenuItem>
                    <MenuItem
                        onClick={() => changedPeriod(ChartPeriod.THIS_YEAR)}>{t('admin.datepicker.this_year')}</MenuItem>
                    <MenuItem
                        onClick={() => changedPeriod(ChartPeriod.LAST_YEAR)}>{t('admin.datepicker.last_year')}</MenuItem>
                    <MenuItem
                        onClick={() => changedPeriod(ChartPeriod.CUSTOM)}>{t('admin.datepicker.custom')}</MenuItem>
                </MenuList>
            </Menu> : ''}
        </div>
    );
}

export default memo(DatePickerMenu);
